import { Link } from "react-router-dom";
import { siteData } from "@/store/siteData";
import useLogoUrl from "@/hooks/useLogoUrl";
import type { UserRole } from "@/utils/userStore";

export type Tab =
  | "dashboard" | "homepage" | "about" | "products" | "partners"
  | "navigation" | "messages" | "settings" | "users" | "changelog";

interface AdminSidebarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  newMessages: number;
  userRole?: UserRole;
}

// Tab → değişiklik zamanı için siteData anahtarı (boşsa rozet gösterilmez)
const sectionKeyMap: Partial<Record<Tab, string>> = {
  homepage: "yg_hero", about: "yg_about", products: "yg_products", partners: "yg_partners",
};

const allNavItems: { id: Tab; label: string; icon: string; roles?: UserRole[] }[] = [
  { id: "dashboard",   label: "Genel Bakış",        icon: "ri-dashboard-3-line",       roles: ["admin", "editor"] },
  { id: "homepage",    label: "Ana Sayfa",          icon: "ri-home-4-line",            roles: ["admin", "editor"] },
  { id: "about",       label: "Hakkımızda",         icon: "ri-information-line",        roles: ["admin", "editor"] },
  { id: "products",    label: "Ürünlerimiz",        icon: "ri-computer-line",          roles: ["admin", "editor"] },
  { id: "partners",    label: "Stratejik Ortaklar", icon: "ri-links-line",             roles: ["admin", "editor"] },
  { id: "navigation",  label: "Sayfa Yönetimi",     icon: "ri-pages-line",             roles: ["admin"] },
  { id: "messages",    label: "Mesajlar",           icon: "ri-mail-line" },
  { id: "settings",    label: "Site Ayarları",      icon: "ri-settings-3-line",        roles: ["admin", "editor"] },
  { id: "users",       label: "Kullanıcılar",       icon: "ri-shield-user-line",       roles: ["admin"] },
];

function formatRelativeTime(iso: string): string {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)}dk`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}sa`;
    return `${Math.floor(diff / 86400)}g`;
  } catch { return ""; }
}

export default function AdminSidebar({ activeTab, onTabChange, newMessages, userRole = "editor" }: AdminSidebarProps) {
  const lastChanged = siteData.getLastChanged();
  const logoUrl = useLogoUrl();

  const supportAllowedTabs: Tab[] = ["messages", "settings"];
  const navItems = allNavItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });
  const filteredNav = userRole === "support" ? navItems.filter((i) => supportAllowedTabs.includes(i.id)) : navItems;
  const isSupport = userRole === "support";

  return (
    <aside className="w-64 bg-[#0F1729] border-r border-[#2B3654] flex flex-col sticky top-0 h-screen overflow-hidden">
      <div className="px-6 py-6 border-b border-[#2B3654] flex-shrink-0">
        <Link to="/" className="flex items-center gap-3 group">
          <img src={logoUrl} alt="C2TEK" className="h-10 w-auto object-contain" />
        </Link>
        <div className="mt-3">
          <p className="text-[#7B8FAF] text-xs tracking-widest uppercase">Yönetim Paneli</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5 overflow-y-auto">
        {isSupport && (
          <div className="mb-3 mx-1 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <i className="ri-customer-service-2-line text-[#3B82F6] text-sm"></i>
              <span className="text-[#A5B5CC] text-xs font-medium">Destek Ekibi</span>
            </div>
            <p className="text-[#465886] text-xs mt-1">Mesajlar ve canlı destek paneline erişim</p>
          </div>
        )}

        {filteredNav.map((item) => {
          const sectionKey = sectionKeyMap[item.id];
          const lastEdit = sectionKey ? lastChanged[sectionKey] : null;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer text-left ${
                activeTab === item.id
                  ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40"
                  : "text-[#7B8FAF] hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </div>
              <span className="flex-1 truncate">{item.label}</span>
              {item.id === "messages" && newMessages > 0 && (
                <span className="bg-[#3B82F6] text-white text-xs px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">{newMessages}</span>
              )}
              {lastEdit && (
                <span className="text-[#465886] text-xs flex-shrink-0 whitespace-nowrap">{formatRelativeTime(lastEdit)}</span>
              )}
            </button>
          );
        })}

        {userRole === "admin" && (
          <>
            <div className="h-px bg-[#2B3654] my-2"></div>
            <button
              onClick={() => onTabChange("changelog")}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer text-left ${
                activeTab === "changelog"
                  ? "bg-[#3B82F6]/20 text-white border border-[#3B82F6]/40"
                  : "text-[#7B8FAF] hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-history-line text-base"></i>
              </div>
              <span className="flex-1">Değişiklik Günlüğü</span>
              {siteData.getChangeLog().length > 0 && (
                <span className="text-[#465886] text-xs flex-shrink-0">{siteData.getChangeLog().length}</span>
              )}
            </button>
          </>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-[#2B3654] space-y-1.5 flex-shrink-0">
        {!isSupport && (
          <Link
            to="/site-inspector"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/20 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-edit-box-line text-sm"></i></div>
            <span className="flex-1 text-xs">Görsel Editör</span>
          </Link>
        )}
        <Link to="/" className="flex items-center gap-2 text-[#7B8FAF] hover:text-white text-sm transition-colors cursor-pointer px-2 py-1.5 rounded hover:bg-white/5">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-external-link-line text-sm"></i></div>
          <span>Siteyi Görüntüle</span>
        </Link>
      </div>
    </aside>
  );
}
